import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, MessageSquare } from "lucide-react";

export default function StaffMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/threads");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="staff-messages-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Assistenza
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Messaggi dai clienti
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Tutte le conversazioni aperte dai clienti. Clicca su una conversazione per aprire la
          scheda del cliente e rispondere.
        </p>
      </div>

      <div className="rounded-3xl border border-[var(--mp-border)] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--mp-muted)]">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[var(--mp-muted)] flex flex-col items-center gap-3">
            <Inbox className="h-6 w-6" />
            Nessuna conversazione ancora.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--mp-border)]">
            {items.map((t) => {
              const { client, last_message, unread } = t;
              return (
                <li key={client.id} data-testid={`thread-row-${client.id}`}>
                  <Link
                    to={`/dashboard/clienti/${client.id}`}
                    className="flex items-center gap-4 p-5 hover:bg-[var(--mp-dash-bg)] transition-colors"
                  >
                    <div className="h-11 w-11 rounded-full bg-[var(--mp-yellow)] grid place-items-center font-display text-sm border border-[var(--mp-ink)] shrink-0">
                      {(client.name_salone || "?")
                        .split(" ")
                        .map((s) => s[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-display text-lg tracking-tight truncate">
                          {client.name_salone}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mp-muted)] shrink-0">
                          {last_message &&
                            new Date(last_message.created_at).toLocaleString("it-IT", {
                              hour: "2-digit",
                              minute: "2-digit",
                              day: "2-digit",
                              month: "2-digit",
                            })}
                        </div>
                      </div>
                      <div className="text-sm text-[var(--mp-muted)] truncate">
                        <span className="font-medium text-[var(--mp-ink)]">
                          {last_message?.sender_role === "client"
                            ? client.referente_nome || "Cliente"
                            : "Tu"}
                          :
                        </span>{" "}
                        {last_message?.text}
                      </div>
                    </div>
                    {unread > 0 && (
                      <span
                        data-testid={`thread-unread-${client.id}`}
                        className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 rounded-full bg-[var(--mp-primary)] text-white text-[11px] font-bold"
                      >
                        {unread}
                      </span>
                    )}
                    <MessageSquare className="h-4 w-4 text-[var(--mp-muted)]" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
